import { useQuery } from 'react-query';
import { openerOrchestratorAxiosApi } from 'src/Config/axiosApi';

/**
 * Cross-group opener descalce snapshot — read from
 * `manual-order-monitor`'s sweeper via `opener-orchestrator`'s
 * `GET /positions/opener-descalce` (a single Redis GET, no Mongo, no venue
 * call). Poll pattern copied from `api/useOpenerGroupPods.ts`.
 *
 * Served by `openerOrchestratorAxiosApi` (`VITE_OPENER_ORCHESTRATOR_URL`) per
 * the control-plane domain split — the descalce screen is an opener-domain
 * read. The route string is byte-identical (`/positions/opener-descalce`);
 * only the base URL changed. `prediction-arbitrage-manager` no longer serves
 * this route at all, so pointing back at it 404s the whole screen.
 *
 * Wire contract per the approved spec/plan (§3.2). `ruleStatus` is typed as
 * plain `string`, NOT a union: the live `entry_rules` collection only ever
 * carries `active` / `paused` today, and typing a union the DB doesn't back
 * would let the FE assume `completed` / `cancelled` rows can't appear.
 */

export type OpenerDescalceAccountKey = 'prod' | 'test';

export type OpenerDescalceSource =
  | 'venue'
  | 'starting'
  | 'unconfigured'
  | 'error';

export interface OpenerDescalceAccountStatus {
  source: OpenerDescalceSource;
  lastSuccessAt: number | null;
  lastError: string | null;
  redeemableExcluded: number;
}

export interface OpenerDescalceGroup {
  id: string | null;
  name: string | null;
  podsDesired: 'on' | 'off' | 'panicked' | null;
  status: string | null;
  testAccount: boolean;
  eventFinished: boolean;
}

export interface OpenerDescalceVenue {
  polyYes: number;
  polyNo: number;
  kalshiYes: number;
  kalshiNo: number;
  /** `+` = excess YES. */
  signedNet: number;
  /** `|signedNet|`. */
  descalce: number;
  /** More than one rule claims one of this row's markets. */
  ambiguous: boolean;
  /** A leg already resolved at its venue (Kalshi settled it, or Polymarket
   *  flagged it redeemable) — a settlement artefact, not a naked leg. */
  resolving: boolean;
  /** Which venue resolved; `null` when `resolving` is false. Optional: rows
   *  from a backend older than the Kalshi-settlement read omit it. */
  resolvingVenue?: 'kalshi' | 'polymarket' | 'both' | null;
  /** This rule is missing a ticker or a Polymarket leg — the counts above
   *  necessarily come from one venue only. Optional: only rows built from a
   *  live backend snapshot carry it; keep it optional so existing fixtures
   *  that predate this field don't need updating. */
  marketsIncomplete?: boolean;
}

export interface OpenerDescalceBelief {
  kalshiNet: number;
  polyNet: number;
  descalce: number;
  /** Producer (engine) clock — passed through verbatim, never re-stamped. */
  at: number;
}

export interface OpenerDescalceDivergence {
  total: number;
  kalshi: number;
  poly: number;
}

export interface OpenerDescalceRow {
  ruleId: string;
  pairId: string;
  pairName: string;
  ruleStatus: string;
  direction: 'entry';
  account: OpenerDescalceAccountKey;

  group: OpenerDescalceGroup;
  subgroupId: string | null;

  markets: {
    kalshiTicker: string | null;
    polyYesTokenId: string | null;
    polyNoTokenId: string | null;
  };

  /** `null` when this row's account is unreadable. */
  venue: OpenerDescalceVenue | null;
  /** `null` when the engine has published nothing for this rule. */
  belief: OpenerDescalceBelief | null;
  /** `null` whenever `belief` is `null`. */
  divergence: OpenerDescalceDivergence | null;

  maxUnhedgedContracts: number | null;
  hedgeCheckEnabled: boolean;
}

export interface OpenerDescalceSnapshot {
  /** epoch ms, sweeper clock. */
  computedAt: number;
  /** so the FE can age it. */
  pollMs: number;
  /** freshness horizon. */
  staleMs: number;
  accounts: Record<OpenerDescalceAccountKey, OpenerDescalceAccountStatus>;
  rows: OpenerDescalceRow[];
}

/**
 * The one gate both transports use. Shared on purpose: the WS used to drop a
 * payload the REST path accepted verbatim, so a backend that stopped sending
 * `staleMs` would kill the push entirely (every frame dropped) while the poll
 * kept feeding the page the same payload and `useDescalceStale` returned false
 * forever — the "old rendered as fresh" failure the guard exists to prevent,
 * not prevented.
 *
 * Each field is load-bearing for a DIFFERENT failure:
 *  · `computedAt` — the staleness banner ages off it; absent, nothing is ever
 *    stale;
 *  · `rows` — `resolveDescalceViewState` reads `.length` and throws without it;
 *  · `accounts` with at least one entry — an EMPTY object degrades
 *    `unconfigured`/`error` straight into `empty`, the screen asserting "no
 *    naked legs" over a payload that said no such thing. Truthiness is not
 *    enough here: `JSON.stringify` drops `undefined` values silently, which is
 *    exactly how a `{}` would reach the wire;
 *  · `staleMs > 0` — three guarantees at once: the Stale banner, and
 *    `isDataEqual`'s horizon escape (which computes against `NaN` without it,
 *    so the cache can stick to one snapshot for the life of the tab).
 *
 * `pollMs` is deliberately NOT required. Nothing on the render path reads it —
 * only the socket hook, to size its push window — so rejecting a snapshot over
 * it would take the whole screen to an error state for a field the screen does
 * not use. The socket falls back to a default instead, which is the
 * proportionate response to a field only IT consumes.
 */
export function isUsableDescalceSnapshot(
  snapshot: unknown
): snapshot is OpenerDescalceSnapshot {
  const s = snapshot as Partial<OpenerDescalceSnapshot> | null | undefined;
  return (
    !!s &&
    typeof s.computedAt === 'number' &&
    Array.isArray(s.rows) &&
    !!s.accounts &&
    Object.keys(s.accounts).length > 0 &&
    typeof s.staleMs === 'number' &&
    s.staleMs > 0
  );
}

/** Thrown when a 200 carries a payload the page cannot safely render. */
export class OpenerDescalceMalformedSnapshotError extends Error {
  constructor() {
    super('opener-descalce snapshot is missing a required field');
    this.name = 'OpenerDescalceMalformedSnapshotError';
  }
}

export const OPENER_DESCALCE_SNAPSHOT_UNAVAILABLE_REASON =
  'snapshot-unavailable';

/**
 * Thrown by `fetchOpenerDescalce` when the backend 404s with
 * `{ reason: 'snapshot-unavailable' }` — the sweeper hasn't published yet, or
 * its key expired. Distinguished from every OTHER fetch failure so the page
 * can render the named "snapshot unavailable" state instead of a generic
 * error (never a silent FE fallback to another source).
 */
export class OpenerDescalceSnapshotUnavailableError extends Error {
  constructor() {
    super('opener-descalce snapshot unavailable');
    this.name = 'OpenerDescalceSnapshotUnavailableError';
  }
}

export function isSnapshotUnavailableError(error: unknown): boolean {
  return error instanceof OpenerDescalceSnapshotUnavailableError;
}

export const OPENER_DESCALCE_KEY = ['opener-descalce-snapshot'];

export async function fetchOpenerDescalce(): Promise<OpenerDescalceSnapshot> {
  try {
    const { data } =
      await openerOrchestratorAxiosApi.get<OpenerDescalceSnapshot>(
        '/positions/opener-descalce'
      );
    // Fail LOUD rather than render it. A snapshot missing one of these makes
    // the page lie quietly — stale data with no Stale banner — and this screen
    // would rather show an error than a confident wrong answer.
    if (!isUsableDescalceSnapshot(data)) {
      throw new OpenerDescalceMalformedSnapshotError();
    }
    return data;
  } catch (err) {
    const axiosErr = err as {
      response?: { status?: number; data?: { reason?: string } };
    };
    if (
      axiosErr.response?.status === 404 &&
      axiosErr.response?.data?.reason ===
        OPENER_DESCALCE_SNAPSHOT_UNAVAILABLE_REASON
    ) {
      throw new OpenerDescalceSnapshotUnavailableError();
    }
    throw err;
  }
}

/**
 * How far ahead of our own clock a snapshot may be stamped before we stop
 * believing it is "newer". Producer and browser clocks drift, and the sweep is
 * 15s, so a minute is generous for honest skew and short enough that a genuine
 * clock fault resolves itself on the next read.
 */
export const CLOCK_SKEW_ALLOWANCE_MS = 60_000;

/** Cadence while the `/descalce` socket is delivering: a safety net only. */
export const DESCALCE_POLL_MS_PUSH_ACTIVE = 60_000;

/** Cadence with no push — the sole transport, so it stays under the 15s sweep. */
export const DESCALCE_POLL_MS_NO_PUSH = 5_000;

export interface UseOpenerDescalceOptions {
  /**
   * The `/descalce` socket has delivered a frame recently
   * (`useOpenerDescalceSocket().pushActive`). Defaults to `false` — a caller
   * that does not wire the socket up keeps the original 5s poll and the
   * screen behaves exactly as it did before the push transport existed.
   */
  pushActive?: boolean;
}

/**
 * The snapshot query. Owns the cache slot the `/descalce` socket writes into,
 * so the page reads ONE piece of state no matter which transport filled it.
 *
 * Cadence follows the push, it does not race it. While frames are arriving the
 * REST read drops to a slow safety net; the instant they stop (gateway pod
 * rolling, socket silently half-open, sweeper dead) it returns to 5s — under
 * the 15s sweep, so nothing is missed. `retry: false` so a 404 (the expected
 * pre-first-sweep state) renders immediately instead of retrying three times.
 *
 * 🚨 The poll is never removed outright. Redis pub/sub has no retention and no
 * delivery guarantee, and on THIS screen an empty table asserts "no naked
 * legs" — a transport that can go quiet without saying so must have a second
 * one behind it.
 */
export function useOpenerDescalce(options: UseOpenerDescalceOptions = {}) {
  const { pushActive = false } = options;
  return useQuery<OpenerDescalceSnapshot>(
    OPENER_DESCALCE_KEY,
    fetchOpenerDescalce,
    {
      refetchInterval: pushActive
        ? DESCALCE_POLL_MS_PUSH_ACTIVE
        : DESCALCE_POLL_MS_NO_PUSH,
      refetchOnWindowFocus: true,
      keepPreviousData: true,
      retry: false,
      // Mirror of the monotonic guard the socket applies on its own writes.
      // A REST fetch started before a pushed frame landed still resolves
      // afterwards, and react-query would dispatch its own success and walk
      // the snapshot BACKWARDS — rows that just appeared would vanish until
      // the next sweep. Returning `true` here makes react-query keep the
      // newer cached object instead of replacing it.
      isDataEqual: (oldData, newData) => {
        if (!oldData || !newData) return false;
        if (
          typeof oldData.computedAt !== 'number' ||
          typeof newData.computedAt !== 'number'
        ) {
          return false;
        }
        // Escape hatches FIRST — both cover the same failure, a cached
        // snapshot the backend can never beat, which would freeze the table
        // for the life of the tab. The banner would say Stale (the safe
        // direction), but it would never self-heal.
        //
        //  · the cache is past its own freshness horizon — a producer that
        //    restarted on a clock-skewed node keeps stamping into the past;
        //  · the cache is stamped in the FUTURE beyond any plausible skew —
        //    the mirror case, and the one a horizon check alone cannot see,
        //    because a future stamp never looks old.
        const now = Date.now();
        if (now - oldData.computedAt > oldData.staleMs) return false;
        if (oldData.computedAt - now > CLOCK_SKEW_ALLOWANCE_MS) return false;

        // `>`, not `>=`: at an identical `computedAt` the two snapshots are
        // genuinely different objects and the newer arrival is the one to keep.
        return oldData.computedAt > newData.computedAt;
      }
    }
  );
}
